/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { Periods } from 'cio/utils/metrics/common';
import EnvironmentModel from 'cio/models/environment';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import { CampaignState } from 'cio/models/campaign';
import ApiService from 'cio/services/api';
import MetricsService from 'cio/services/metrics';
import { ProcessedMetricItem } from 'cio/utils/metrics/analysis';
import LeaderboardCardItem from 'cio/components/leaderboard-card/item';
import { SortDirection } from 'cio/utils/sort';
import { TimeRange } from 'cio/utils/time';
import SettingsService from 'cio/services/settings';

interface DashboardPageLeaderboardCardArgs {
  environmentModel: EnvironmentModel;
  timeRange: TimeRange;
  resolution: Periods;
  channels: { label: string; value: string; icon: string }[];
  isLoading: boolean;
  isUpdating: boolean;
  onTrackNavigate: (data: Record<string, any>) => unknown;
}

export default class DashboardPageLeaderboardCard extends Component<DashboardPageLeaderboardCardArgs> {
  @service api!: ApiService;
  @service('metrics') metricsService!: MetricsService;
  @service settings!: SettingsService;

  tabs = [
    {
      label: 'Sent',
      value: 'sent',
      sortValue: 'total_sent',
      totalValue: 'total_sent',
      blankStateLabel: 'sends',
      sortLabel: 'sends',
      isRate: false,
      icon: 'send-auto',
    },
    {
      label: 'Converted',
      value: 'converted',
      sortValue: 'converted_rate',
      totalValue: 'total_converted',
      blankStateLabel: 'conversions',
      sortLabel: 'conversions',
      isRate: true,
      icon: 'goal',
    },
    {
      label: 'Opened',
      value: 'opened',
      sortValue: 'opened_rate',
      totalValue: 'total_opened',
      blankStateLabel: 'opens',
      sortLabel: 'opens',
      isRate: true,
      icon: 'error',
    },
    {
      label: 'Clicked',
      value: 'clicked',
      sortValue: 'clicked_rate',
      totalValue: 'total_clicked',
      blankStateLabel: 'tracked link clicks',
      sortLabel: 'clicks',
      isRate: true,
      icon: 'cursor',
    },
  ];

  @tracked tab = this.tabs[0].value;
  @tracked sortDirection: SortDirection = SortDirection.Descending;

  @action
  onInsert() {
    this.runReport.perform();
  }

  @tracked items: LeaderboardCardItem[] = [];

  get channelValues() {
    return this.args.channels.map((c) => c.value);
  }

  runReport = task({ restartable: true }, async () => {
    let result = await this.metricsService.fetchAnalysisMetrics(
      this.args.timeRange,
      this.args.resolution,
      '',
      [],
      [CampaignState.Running, CampaignState.Archived, CampaignState.Stopped],
      ['campaign', 'newsletter', 'broadcast'],
      this.channelValues,
      { excludeNewslettersWithoutSends: true, refreshData: true },
    );

    this.items = (result.data ?? []).map((m: ProcessedMetricItem) => ({
      id: m._id,
      icon: m.icon,
      type: m.type,
      name: m.name,
      channel: m.channel ?? undefined,
      data: m.data,
      conversion: m.conversion,
    }));
  });

  @action
  trackNavigate(destination: string, sortDirection: SortDirection, activeTab: string) {
    this.args.onTrackNavigate({
      card: 'leaderboard',
      destination,
      sortDirection,
      metric: activeTab,
    });
  }
}
